import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Search, MessageCircle, ChevronDown, User, Shield, Phone } from "lucide-react"
import { useAvailableUsers } from "@/hooks/useAvailableUsers"
import { useCreateDirectConversation } from "@/hooks/useDirectConversation"
import { toast } from "sonner"

interface UserDropdownProps {
  onConversationCreated: (conversationId: string, recipientName: string) => void
  children: React.ReactNode
}

export const UserDropdown = ({ onConversationCreated, children }: UserDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const { data: availableUsers = [] } = useAvailableUsers()
  const createConversationMutation = useCreateDirectConversation()

  const moradores = availableUsers.filter(user => user.role === 'morador')
  const sindicos = availableUsers.filter(user => user.role === 'sindico')
  const porteiros = availableUsers.filter(user => user.role === 'porteiro')
  
  const filteredMoradores = moradores.filter(morador =>
    morador.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    morador.apartment_number?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleStartConversation = async (userId: string, userName: string) => {
    try {
      setIsOpen(false)
      const conversationId = await createConversationMutation.mutateAsync({
        participant_user_id: userId
      })
      
      onConversationCreated(conversationId, userName)
      toast.success(`Conversa iniciada com ${userName}`)
    } catch (error) {
      console.error("Erro ao criar conversa:", error)
      toast.error("Erro ao iniciar conversa")
    }
  }

  const getUserIcon = (role: string) => {
    switch (role) {
      case 'sindico': return Shield
      case 'porteiro': return Phone
      default: return User
    }
  }

  const getUserRoleLabel = (role: string) => {
    switch (role) {
      case 'sindico': return 'Síndico'
      case 'porteiro': return 'Portaria'
      case 'morador': return 'Morador'
      default: return 'Usuário'
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 max-h-96 overflow-y-auto" align="start">
        <DropdownMenuLabel>
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Selecionar Destinatário
          </div>
        </DropdownMenuLabel>
        
        <div className="p-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Síndicos */}
        {sindicos.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground px-2">
              SÍNDICOS
            </DropdownMenuLabel>
            {sindicos.map((sindico) => {
              const Icon = getUserIcon(sindico.role)
              return (
                <DropdownMenuItem
                  key={sindico.id}
                  onClick={() => handleStartConversation(sindico.id, sindico.full_name)}
                  className="flex items-center gap-3 p-3 cursor-pointer"
                  disabled={createConversationMutation.isPending}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-100 text-blue-700">
                      {sindico.full_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{sindico.full_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Icon className="h-3 w-3" />
                      {getUserRoleLabel(sindico.role)}
                    </p>
                  </div>
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Porteiros */}
        {porteiros.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground px-2">
              PORTARIA
            </DropdownMenuLabel>
            {porteiros.map((porteiro) => {
              const Icon = getUserIcon(porteiro.role)
              return (
                <DropdownMenuItem
                  key={porteiro.id}
                  onClick={() => handleStartConversation(porteiro.id, porteiro.full_name)}
                  className="flex items-center gap-3 p-3 cursor-pointer"
                  disabled={createConversationMutation.isPending}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-green-100 text-green-700">
                      {porteiro.full_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{porteiro.full_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Icon className="h-3 w-3" />
                      {getUserRoleLabel(porteiro.role)}
                    </p>
                  </div>
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Moradores */}
        {filteredMoradores.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground px-2">
              MORADORES ({filteredMoradores.length})
            </DropdownMenuLabel>
            {filteredMoradores.slice(0, 10).map((morador) => {
              const Icon = getUserIcon(morador.role)
              return (
                <DropdownMenuItem
                  key={morador.id}
                  onClick={() => handleStartConversation(morador.id, morador.full_name)}
                  className="flex items-center gap-3 p-3 cursor-pointer"
                  disabled={createConversationMutation.isPending}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {morador.full_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{morador.full_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Icon className="h-3 w-3" />
                      {morador.apartment_number ? `Apt ${morador.apartment_number}` : 'Sem apartamento'}
                    </p>
                  </div>
                </DropdownMenuItem>
              )
            })}
            {filteredMoradores.length > 10 && (
              <DropdownMenuLabel className="text-xs text-muted-foreground px-2">
                ... e mais {filteredMoradores.length - 10} moradores
              </DropdownMenuLabel>
            )}
          </>
        )}

        {searchTerm && filteredMoradores.length === 0 && sindicos.length === 0 && porteiros.length === 0 && (
          <DropdownMenuLabel className="text-center py-4 text-muted-foreground">
            Nenhum usuário encontrado
          </DropdownMenuLabel>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}